import { useState, useEffect } from 'react';
import { getCenters, createCenter, updateCenter, deleteCenter } from '../../lib/db';
import { Center } from '../../types';
import { Plus, Pencil, Trash2, Building2, X, Check } from 'lucide-react';

export function CentersManager() {
  const [centers, setCenters] = useState<Center[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', isCra: false });
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const data = await getCenters();
      setCenters(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    try {
      await createCenter({ name: form.name.trim(), isCra: form.isCra });
      setForm({ name: '', isCra: false });
      setAdding(false);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleUpdate = async (id: string) => {
    if (!form.name.trim()) return;
    try {
      await updateCenter(id, { name: form.name.trim(), isCra: form.isCra });
      setEditing(null);
      setForm({ name: '', isCra: false });
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`¿Eliminar el centro "${name}"? Se eliminarán TODOS los datos asociados.`)) return;
    try {
      await deleteCenter(id);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const startEdit = (center: Center) => {
    setEditing(center.id);
    setForm({ name: center.name, isCra: center.isCra });
    setAdding(false);
  };

  const cancel = () => {
    setEditing(null);
    setAdding(false);
    setForm({ name: '', isCra: false });
  };

  if (loading) {
    return <div className="p-6 text-slate-500">Cargando centros...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Gestión de Centros
        </h3>
        {!adding && (
          <button
            onClick={() => { setAdding(true); setEditing(null); setForm({ name: '', isCra: false }); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Nuevo Centro
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {adding && (
        <div className="p-4 bg-white rounded-lg border border-indigo-200 shadow-sm">
          <div className="flex items-center gap-3">
            <input
              autoFocus
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Nombre del centro"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
            <label className="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap">
              <input
                type="checkbox"
                checked={form.isCra}
                onChange={e => setForm({ ...form, isCra: e.target.checked })}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Es CRA
            </label>
            <button onClick={handleAdd} className="p-2 text-green-600 hover:bg-green-50 rounded-lg"><Check className="w-4 h-4" /></button>
            <button onClick={cancel} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {centers.map(center => (
          <div key={center.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
            {editing === center.id ? (
              <>
                <input
                  autoFocus
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  onKeyDown={e => e.key === 'Enter' && handleUpdate(center.id)}
                />
                <label className="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={form.isCra}
                    onChange={e => setForm({ ...form, isCra: e.target.checked })}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  CRA
                </label>
                <button onClick={() => handleUpdate(center.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                <button onClick={cancel} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="flex-1 text-sm font-medium text-slate-700">{center.name}</span>
                {center.isCra && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">CRA</span>
                )}
                <button onClick={() => startEdit(center)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(center.id, center.name)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        ))}
        {centers.length === 0 && !adding && (
          <p className="text-sm text-slate-500 text-center py-8">No hay centros configurados. Crea el primer centro para empezar.</p>
        )}
      </div>
    </div>
  );
}
